/**
 * Converts old public upload URLs to API URLs for production compatibility
 * @param url - The image URL (could be old format, new format, or external URL)
 * @returns The converted API URL or the original URL if it's external
 */
export function convertUploadUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  // If it's already an API URL with query param, return as-is
  if (url.startsWith('/api/upload?filename=')) {
    return url;
  }

  // If it's an old API URL with path param, convert to query param format
  if (url.startsWith('/api/uploads/')) {
    const filename = url.replace('/api/uploads/', '');
    return `/api/upload?filename=${filename}`;
  }

  // If it's an old public upload URL, convert to API URL
  if (url.startsWith('/uploads/')) {
    const filename = url.replace('/uploads/', '');
    return `/api/upload?filename=${filename}`;
  }

  // If it's an external URL (starts with http/https), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If it's a relative path that doesn't start with /uploads/, assume it's a static asset
  // and return as-is (e.g., /pc-members/image.jpg)
  return url;
}

/**
 * Extracts the filename from an upload URL (both old and new formats)
 * @param url - The upload URL
 * @returns The filename or null if not an upload URL
 */
export function getUploadFilename(url: string | null | undefined): string | null {
  if (!url) return null;

  if (url.startsWith('/api/upload?filename=')) {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    return urlParams.get('filename');
  }

  if (url.startsWith('/api/uploads/')) {
    return url.replace('/api/uploads/', '');
  }

  if (url.startsWith('/uploads/')) {
    return url.replace('/uploads/', '');
  }

  return null;
}
